import { ChevronRight } from "@untitledui/icons";
import type { FC, ReactNode } from "react";
import { Link as AriaLink } from "react-aria-components";
import { cx } from "@/utils/cx";

export type BreadcrumbItem = {
	label: string;
	href?: string;
	icon?: ReactNode;
	onClick?: () => void;
};

type BreadcrumbProps = {
	items: BreadcrumbItem[];
	className?: string;
	ariaLabel?: string;
};

function BreadcrumbLink({ item }: { item: BreadcrumbItem }) {
	const className = cx(
		"flex items-center gap-2 rounded-md font-medium text-sm text-tertiary transition-colors",
		"outline-focus-ring hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-2"
	);

	if (item.onClick) {
		return (
			<button
				className={cx(className, "cursor-pointer border-0 bg-transparent p-0")}
				onClick={item.onClick}
				type="button"
			>
				{item.icon}
				{item.label}
			</button>
		);
	}

	return (
		<AriaLink className={className} href={item.href ?? "#"}>
			{item.icon}
			{item.label}
		</AriaLink>
	);
}

export const Breadcrumb: FC<BreadcrumbProps> = ({
	items,
	className,
	ariaLabel = "Breadcrumb",
}) => (
	<nav
		aria-label={ariaLabel}
		className={cx("flex items-center gap-2", className)}
	>
		<ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
			{items.map((item, index) => {
				const isLast = index === items.length - 1;

				return (
					<li
						className="flex items-center gap-2"
						key={`${item.label}-${index}`}
					>
						{index > 0 && (
							<ChevronRight
								aria-hidden="true"
								className="size-4 shrink-0 text-fg-quaternary"
							/>
						)}

						{isLast ? (
							<span
								aria-current="page"
								className="flex items-center gap-2 font-medium text-secondary text-sm"
							>
								{item.icon}
								{item.label}
							</span>
						) : (
							<BreadcrumbLink item={item} />
						)}
					</li>
				);
			})}
		</ol>
	</nav>
);
